import React, { useState, useEffect, useRef } from 'react';
import { Save, User, Shield, Bell, Globe, Palette, Database, Key, Plus, Trash2, Image, Link as LinkIcon, Upload, Monitor } from 'lucide-react';
import { getDashboardContent, updateDashboardContent } from '../utils/dashboardContentApi';
import { getSiteMeta, updateSiteMeta } from '../utils/siteMetaApi';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import QuillEditor from '../components/QuillEditor';
import { uploadFileToPublic } from '../utils/fileUpload';
import { S3_BASE_URL } from '../config/api';
import SubmitNotification from '../components/SubmitNotification';
import { changeUserPassword } from '../utils/userApi';

const Settings = () => {
  const { user, userSites } = useAuth();
  const [activeTab, setActiveTab] = useState('notifications');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [siteMetaLoading, setSiteMetaLoading] = useState(false);
  const [siteMetaSaving, setSiteMetaSaving] = useState(false);
  const [submitNotification, setSubmitNotification] = useState(null);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [siteMetaError, setSiteMetaError] = useState(null);
  const [siteMetaSuccessMessage, setSiteMetaSuccessMessage] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingPdfLogo, setUploadingPdfLogo] = useState(false);
  const [uploadingMetaImages, setUploadingMetaImages] = useState({});
  const [updatePasswordSaving, setUpdatePasswordSaving] = useState(false);
  const [updatePasswordForm, setUpdatePasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: ''
  });
  
  // Refs for ReactQuill components to avoid findDOMNode warning
  const titleQuillRef = useRef(null);
  const descriptionQuillRef = useRef(null);
  
  // Get site_id from user login data
  const currentSiteId = userSites?.site_id || user?.site_id || 1;
  const [formData, setFormData] = useState({
    firstName: 'John',
    lastName: 'Doe',
    email: 'john.doe@example.com',
    phone: '+1 (555) 123-4567',
    company: 'Acme Corp',
    timezone: 'UTC-5',
    language: 'en',
    title: '',
    description: '',
    meta: [],
    notifications: {
      email: true,
      push: false,
      sms: true
    },
    siteSetup: {
      site_id: '',
      logo: '',
      pdfLogo: '',
      titlePageBackgroundColor: '#ffffff',
      titlePageTextColor: '#000000',
      replyEmail: ''
    }
  });

  // Quill editor modules configuration
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['link'],
      ['clean']
    ]
  };

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'color', 'background',
    'align',
    'link'
  ];

  // Fetch dashboard content on component mount
  useEffect(() => {
    const fetchDashboardContent = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getDashboardContent();
        
        if (response.success && response.data) {
          console.log('dashboard content response:', response);
          console.log('dashboard content data:', response.data.data);
          console.log('dashboard content meta field:', response.data.data.meta);
          console.log('dashboard content meta isArray:', Array.isArray(response.data.data.meta));
          console.log('dashboard content meta length:', response.data.data.meta?.length);

          setFormData(prev => ({
            ...prev,
            title: response.data.data.title || '',
            description: response.data.data.description || '',
            meta: Array.isArray(response.data.data.meta) ? response.data.data.meta : []
          }));
        } else if (!response.success) {
          // Don't show error if content doesn't exist yet
          console.log('No dashboard content found, using defaults');
        }

        
      } catch (err) {
        console.error('Error fetching dashboard content:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardContent();
  }, []);

  // Fetch site meta data on component mount
  useEffect(() => {
    const fetchSiteMeta = async () => {
      if (!currentSiteId) {
        console.log('No site ID available, skipping site meta fetch');
        return;
      }

      setSiteMetaLoading(true);
      setSiteMetaError(null);
      try {
        const response = await getSiteMeta(currentSiteId);
        
        if (response.success && response.data) {
          console.log('site meta response for site', currentSiteId, response);
          console.log('site meta data:', response.data.data.config);
          console.log('site meta meta field:', response.data.data.meta);
          
          setFormData(prev => ({
            ...prev,
            siteSetup: {
              ...prev.siteSetup,
              site_id: currentSiteId,
              logo: response.data.data.config.logo || '',
              pdfLogo: response.data.data.config.pdf_logo || '',
              titlePageBackgroundColor: response.data.data.config.title_page_background_color || '#ffffff',
              titlePageTextColor: response.data.data.config.title_page_text_color || '#000000',
              replyEmail: response.data.data.config.reply_email || ''
            }
            // Don't override meta data here - it should come from dashboard content API
          }));
        } else if (!response.success) {
          // Don't show error if content doesn't exist yet
          console.log(`No site meta found for site ${currentSiteId}, using defaults`);
        }
        
      } catch (err) {
        console.error('Error fetching site meta:', err);
      } finally {
        setSiteMetaLoading(false);
      }
    };

    fetchSiteMeta();
  }, [currentSiteId]);

  // Switch to siteSetup tab if user is on notifications tab and their role is 'user'
  useEffect(() => {
    if (activeTab === 'notifications' && user?.role === 'user') {
      setActiveTab('siteSetup');
    }
  }, [user?.role, activeTab]);

  const allTabs = [
    //{ id: 'profile', name: 'Profile', icon: User },
    //{ id: 'security', name: 'Security', icon: Shield },
    { id: 'notifications', name: 'Dashboard Setup', icon: Bell },
    { id: 'siteSetup', name: 'Site Setup', icon: Monitor },
    { id: 'updatePassword', name: 'Update Password', icon: Key },
    //{ id: 'appearance', name: 'Appearance', icon: Palette },
    //{ id: 'integrations', name: 'Integrations', icon: Database }
  ];

  // Filter tabs based on user role - hide Dashboard Setup for users with role 'user'
  const tabs = allTabs.filter(tab => {
    if (tab.id === 'notifications' && user?.role === 'user') {
      return false;
    }

    if (tab.id === 'siteSetup' && user?.role === 'admin') {
      return false;
    }
    return true;
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNotificationChange = (type, value) => {
    setFormData(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [type]: value
      }
    }));
  };

  const handleSiteSetupChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      siteSetup: {
        ...prev.siteSetup,
        [field]: value
      }
    }));
  };

  const handleUpdatePasswordChange = (field, value) => {
    setUpdatePasswordForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileUpload = async (field, file) => {
    if (!file) return;

    console.log(`🔄 Starting upload for ${field}:`, {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      field: field
    });

    // Set appropriate loading state
    if (field === 'logo') {
      setUploadingLogo(true);
    } else if (field === 'pdfLogo') {
      setUploadingPdfLogo(true);
    }

    try {
      console.log(`🔄 Uploading ${field}:`, file.name);
      
      // Check if auth token exists
      const token = localStorage.getItem('authToken');
      console.log('🔑 Auth token exists:', !!token);
      
      // Upload file to server
      const uploadedFilePath = await uploadFileToPublic(file, 'sites');
      
      console.log(`✅ ${field} uploaded successfully:`, uploadedFilePath);
      
      // Update form data with the uploaded file path
      handleSiteSetupChange(field, uploadedFilePath);
      
      // Show success message
      setSiteMetaSuccessMessage(`${field === 'logo' ? 'Logo' : 'PDF Logo'} uploaded successfully!`);
      setTimeout(() => setSiteMetaSuccessMessage(''), 3000);
      
    } catch (error) {
      console.error(`❌ Error uploading ${field}:`, error);
      setSiteMetaError(`Failed to upload ${field === 'logo' ? 'logo' : 'PDF logo'}: ${error.message}`);
      setTimeout(() => setSiteMetaError(''), 5000);
    } finally {
      // Clear loading state
      if (field === 'logo') {
        setUploadingLogo(false);
      } else if (field === 'pdfLogo') {
        setUploadingPdfLogo(false);
      }
    }
  };

  const handleMetaImageUpload = async (metaIndex, file) => {
    if (!file) return;

    const uploadKey = `meta_${metaIndex}`;
    
    console.log(`🔄 Starting meta image upload for card ${metaIndex}:`, {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      metaIndex: metaIndex
    });

    // Set loading state for this specific meta card
    setUploadingMetaImages(prev => ({ ...prev, [uploadKey]: true }));

    try {
      // Upload file to server
      const uploadedFilePath = await uploadFileToPublic(file, 'meta-images');
      
      console.log(`✅ Meta image uploaded successfully:`, uploadedFilePath);
      
      // Update the specific meta item with the uploaded file path
      updateMetaItem(metaIndex, 'meta_attach', uploadedFilePath);
      
      // Show success message
      setSuccessMessage(`Meta card image uploaded successfully!`);
      setTimeout(() => setSuccessMessage(''), 3000);
      
    } catch (error) {
      console.error(`❌ Error uploading meta image:`, error);
      setError(`Failed to upload meta card image: ${error.message}`);
      setTimeout(() => setError(''), 5000);
    } finally {
      // Clear loading state for this specific meta card
      setUploadingMetaImages(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission
    console.log('Form submitted:', formData);
  };

  const handleDashboardContentSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccessMessage('');
    setSubmitNotification({ type: 'loading', message: 'Saving dashboard content...' });

    console.log('formData', formData);
    console.log('meta field type:', typeof formData.meta, 'is array:', Array.isArray(formData.meta));
    try {
      const response = await updateDashboardContent({
        title: formData.title,
        description: formData.description,
        meta: Array.isArray(formData.meta) ? formData.meta : []
      });

      if (response.success) {
        setSuccessMessage('Dashboard content saved successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
        setSubmitNotification({ type: 'success', message: 'Dashboard content saved successfully!' });
      } else {
        setError(response.error || 'Failed to save dashboard content');
        setSubmitNotification({ type: 'error', message: response.error || 'Failed to save dashboard content' });
      }
      
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError('An error occurred while saving dashboard content');
      console.error('Error saving dashboard content:', err);
      setSubmitNotification({ type: 'error', message: 'An error occurred while saving dashboard content' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
    }
  };

  const handleSiteMetaSubmit = async (e) => {
    e.preventDefault();
    setSiteMetaSaving(true);
    setSiteMetaError(null);
    setSiteMetaSuccessMessage('');
    setSubmitNotification({ type: 'loading', message: 'Saving site setup...' });

    console.log('site meta formData', formData.siteSetup);
    try {
      const response = await updateSiteMeta({
        site_id: currentSiteId,
        logo: formData.siteSetup.logo,
        pdf_logo: formData.siteSetup.pdfLogo,
        title_page_background_color: formData.siteSetup.titlePageBackgroundColor,
        title_page_text_color: formData.siteSetup.titlePageTextColor,
        reply_email: formData.siteSetup.replyEmail,
        meta: Array.isArray(formData.meta) ? formData.meta : []
      }, currentSiteId);

      if (response.success) {
        setSiteMetaSuccessMessage('Site setup saved successfully!');
        setTimeout(() => setSiteMetaSuccessMessage(''), 3000);
        setSubmitNotification({ type: 'success', message: 'Site setup saved successfully!' });
      } else {
        setSiteMetaError(response.error || 'Failed to save site setup');
        setSubmitNotification({ type: 'error', message: response.error || 'Failed to save site setup' });
      }
      
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setSiteMetaError('An error occurred while saving site setup');
      console.error('Error saving site setup:', err);
      setSubmitNotification({ type: 'error', message: 'An error occurred while saving site setup' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSiteMetaSaving(false);
    }
  };

  const handleUpdatePasswordSubmit = async (e) => {
    e.preventDefault();

    if (!updatePasswordForm.currentPassword || !updatePasswordForm.newPassword || !updatePasswordForm.confirmNewPassword) {
      setSubmitNotification({ type: 'error', message: 'Please fill in all password fields.' });
      return;
    }

    if (updatePasswordForm.newPassword !== updatePasswordForm.confirmNewPassword) {
      setSubmitNotification({ type: 'error', message: 'New password and confirmation do not match.' });
      return;
    }

    setUpdatePasswordSaving(true);
    setSubmitNotification({ type: 'loading', message: 'Updating password...' });

    try {
      const response = await changeUserPassword({
        current_password: updatePasswordForm.currentPassword,
        new_password: updatePasswordForm.newPassword,
        new_password_confirmation: updatePasswordForm.confirmNewPassword
      });

      if (response.success) {
        setUpdatePasswordForm({
          currentPassword: '',
          newPassword: '',
          confirmNewPassword: ''
        });
        setSubmitNotification({ type: 'success', message: 'Password updated successfully!' });
      } else {
        setSubmitNotification({ type: 'error', message: response.error || 'Failed to update password' });
      }
    } catch (err) {
      console.error('Error updating password:', err);
      setSubmitNotification({ type: 'error', message: 'An error occurred while updating password' });
    } finally {
      setUpdatePasswordSaving(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Meta handlers
  const addMetaItem = () => {
    setFormData(prev => ({
      ...prev,
      meta: [...(prev.meta || []), {
        meta_key: '',
        meta_value: '',
        meta_link: '',
        meta_link_target: 0,
        meta_attach: ''
      }]
    }));
  };

  const removeMetaItem = (index) => {
    setFormData(prev => ({
      ...prev,
      meta: (prev.meta || []).filter((_, i) => i !== index)
    }));
  };

  const updateMetaItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      meta: (prev.meta || []).map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const renderProfileTab = () => (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            First Name
          </label>
          <input
            type="text"
            value={formData.firstName}
            onChange={(e) => handleInputChange('firstName', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Last Name
          </label>
          <input
            type="text"
            value={formData.lastName}
            onChange={(e) => handleInputChange('lastName', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email
          </label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Phone
          </label>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Company
          </label>
          <input
            type="text"
            value={formData.company}
            onChange={(e) => handleInputChange('company', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Timezone
          </label>
          <select
            value={formData.timezone}
            onChange={(e) => handleInputChange('timezone', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="UTC-8">UTC-8 (Pacific Time)</option>
            <option value="UTC-7">UTC-7 (Mountain Time)</option>
            <option value="UTC-6">UTC-6 (Central Time)</option>
            <option value="UTC-5">UTC-5 (Eastern Time)</option>
            <option value="UTC+0">UTC+0 (GMT)</option>
            <option value="UTC+1">UTC+1 (Central European Time)</option>
          </select>
        </div>
      </div>

      <div className="flex justify-end">
        <button type="submit" className="btn-primary flex items-center space-x-2">
          <Save className="h-4 w-4" />
          <span>Save Changes</span>
        </button>
      </div>
    </form>
  );

  const renderSecurityTab = () => (
    <div className="space-y-6">
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Change Password</h3>
        <form className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Current Password
            </label>
            <input
              type="password"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              New Password
            </label>
            <input
              type="password"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Confirm New Password
            </label>
            <input
              type="password"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          
          <button type="submit" className="btn-primary">
            Update Password
          </button>
        </form>
      </div>

      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Two-Factor Authentication</h3>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600">Add an extra layer of security to your account</p>
            <p className="text-xs text-gray-500 mt-1">Currently disabled</p>
          </div>
          <button className="btn-primary">Enable 2FA</button>
        </div>
      </div>

      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Active Sessions</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <p className="text-sm font-medium text-gray-900">Chrome on Windows</p>
              <p className="text-xs text-gray-500">Last active: 2 hours ago</p>
            </div>
            <button className="text-sm text-red-600 hover:text-red-700">Revoke</button>
          </div>
          
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <p className="text-sm font-medium text-gray-900">Safari on iPhone</p>
              <p className="text-xs text-gray-500">Last active: 1 day ago</p>
            </div>
            <button className="text-sm text-red-600 hover:text-red-700">Revoke</button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderUpdatePasswordTab = () => (
    <div className="space-y-6">
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Change Password</h3>
        <form onSubmit={handleUpdatePasswordSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Current Password
            </label>
            <input
              type="password"
              value={updatePasswordForm.currentPassword}
              onChange={(e) => handleUpdatePasswordChange('currentPassword', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              disabled={updatePasswordSaving}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              New Password
            </label>
            <input
              type="password"
              value={updatePasswordForm.newPassword}
              onChange={(e) => handleUpdatePasswordChange('newPassword', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              disabled={updatePasswordSaving}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Confirm New Password
            </label>
            <input
              type="password"
              value={updatePasswordForm.confirmNewPassword}
              onChange={(e) => handleUpdatePasswordChange('confirmNewPassword', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              disabled={updatePasswordSaving}
            />
          </div>

          <button type="submit" className="btn-primary" disabled={updatePasswordSaving}>
            {updatePasswordSaving ? 'Updating...' : 'Update Password'}
          </button>
        </form>
      </div>
    </div>
  );

  const renderNotificationsTab = () => (
    <form onSubmit={handleDashboardContentSubmit} className="space-y-6">
      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md">
          {successMessage}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {error}
        </div>
      )}

      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Dashboard Information</h3>
        {loading ? (
          <div className="flex justify-center items-center py-8">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title
              </label>
              <div className="bg-white rounded-md border border-gray-300 quill-container-custom">
                <QuillEditor
                  ref={titleQuillRef}
                  value={formData.title || ''}
                  onChange={(value) => handleInputChange('title', value)}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Enter dashboard title (e.g., Welcome to Our Dashboard)"
                  readOnly={saving}
                  theme="snow"
                  className="h-40"
                  preserveWhitespace={true}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <div className="bg-white rounded-md border border-gray-300 quill-container-custom">
                <QuillEditor
                  ref={descriptionQuillRef}
                  value={formData.description || ''}
                  onChange={(value) => handleInputChange('description', value)}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Enter a detailed dashboard description with formatting..."
                  readOnly={saving}
                  theme="snow"
                  className="h-64"
                  preserveWhitespace={true}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Meta Items Section */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Dashboard Meta Cards</h3>
          <button
            type="button"
            onClick={addMetaItem}
            className="btn-secondary flex items-center space-x-2"
            disabled={saving}
          >
            <Plus className="h-4 w-4" />
            <span>Add Meta Card</span>
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-8">
            <LoadingSpinner />
          </div>
        ) : (() => {
          console.log('Meta cards render - loading:', loading);
          console.log('Meta cards render - formData.meta:', formData.meta);
          console.log('Meta cards render - isArray:', Array.isArray(formData.meta));
          console.log('Meta cards render - length:', formData.meta?.length);
          
          return (!formData.meta || !Array.isArray(formData.meta) || formData.meta.length === 0) ? (
            <div className="text-center py-8 text-gray-500">
              <p>No meta cards added yet. Click "Add Meta Card" to create one.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {formData.meta.map((meta, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-md font-medium text-gray-700">Meta Card #{index + 1}</h4>
                    <button
                      type="button"
                      onClick={() => removeMetaItem(index)}
                      className="text-red-600 hover:text-red-700 flex items-center space-x-1"
                      disabled={saving}
                    >
                      <Trash2 className="h-4 w-4" />
                      <span>Remove</span>
                    </button>
                  </div>

                  <div className="space-y-4">
                    {/* Meta Key */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Meta Key
                      </label>
                      <input
                        type="text"
                        value={meta.meta_key || ''}
                        onChange={(e) => updateMetaItem(index, 'meta_key', e.target.value)}
                        placeholder="Enter meta card key"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                        disabled={saving}
                      />
                    </div>

                    {/* Meta Value */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Meta Value
                      </label>
                      <textarea
                        value={meta.meta_value || ''}
                        onChange={(e) => updateMetaItem(index, 'meta_value', e.target.value)}
                        placeholder="Enter meta card value"
                        rows="3"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                        disabled={saving}
                      />
                    </div>

                    {/* Meta Link */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center space-x-1">
                        <LinkIcon className="h-4 w-4" />
                        <span>Link URL</span>
                      </label>
                      <input
                        type="url"
                        value={meta.meta_link || ''}
                        onChange={(e) => updateMetaItem(index, 'meta_link', e.target.value)}
                        placeholder="https://example.com"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                        disabled={saving}
                      />
                    </div>

                    {/* Meta Link Target */}
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={`meta_link_target_${index}`}
                        checked={meta.meta_link_target === 1 || meta.meta_link_target === true}
                        onChange={(e) => updateMetaItem(index, 'meta_link_target', e.target.checked ? 1 : 0)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        disabled={saving}
                      />
                      <label htmlFor={`meta_link_target_${index}`} className="text-sm text-gray-700">
                        Open link in new tab
                      </label>
                    </div>

                    {/* Meta Attachment (Image Upload) */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center space-x-1">
                        <Image className="h-4 w-4" />
                        <span>Image Upload</span>
                      </label>
                      
                      {/* Current Image Preview */}
                      {meta.meta_attach && (
                        <div className="mb-3">
                          <img 
                            src={`${S3_BASE_URL}/${meta.meta_attach}`} 
                            alt="Current Image" 
                            className="h-24 w-24 object-cover rounded-md border border-gray-300"
                          />
                          <p className="text-xs text-gray-500 mt-1">Current image</p>
                        </div>
                      )}

                      {/* File Upload */}
                      <div className="flex items-center space-x-4">
                        <div className="flex-shrink-0">
                          {!meta.meta_attach && (
                            <div className="h-16 w-16 border-2 border-dashed border-gray-300 rounded-md flex items-center justify-center bg-gray-50">
                              <Image className="h-6 w-6 text-gray-400" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          <label className="cursor-pointer">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleMetaImageUpload(index, e.target.files[0])}
                              className="hidden"
                              disabled={saving || uploadingMetaImages[`meta_${index}`]}
                            />
                            <div className={`btn-secondary inline-flex items-center space-x-2 ${saving || uploadingMetaImages[`meta_${index}`] ? 'opacity-50 cursor-not-allowed' : ''}`}>
                              {uploadingMetaImages[`meta_${index}`] ? (
                                <>
                                  <LoadingSpinner />
                                  <span>Uploading...</span>
                                </>
                              ) : (
                                <>
                                  <Upload className="h-4 w-4" />
                                  <span>{meta.meta_attach ? 'Replace Image' : 'Upload Image'}</span>
                                </>
                              )}
                            </div>
                          </label>
                          <p className="text-xs text-gray-500 mt-2">
                            Recommended: PNG, JPG, or GIF format, max 2MB
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          );
        })()}
        </div>

      {/* Save Button */}
      {!loading && (
        <div className="flex justify-end">
          <button 
            type="submit" 
            className="btn-primary flex items-center space-x-2"
            disabled={saving}
          >
            <Save className="h-4 w-4" />
            <span>{saving ? 'Saving...' : 'Save All Changes'}</span>
          </button>
        </div>
      )}
      {/** 
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Notification Preferences</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">Email Notifications</p>
              <p className="text-xs text-gray-500">Receive notifications via email</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.notifications.email}
                onChange={(e) => handleNotificationChange('email', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
            </label>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">Push Notifications</p>
              <p className="text-xs text-gray-500">Receive push notifications in browser</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.notifications.push}
                onChange={(e) => handleNotificationChange('push', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
            </label>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">SMS Notifications</p>
              <p className="text-xs text-gray-500">Receive notifications via SMS</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.notifications.sms}
                onChange={(e) => handleNotificationChange('sms', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
            </label>
          </div>
        </div>
      </div>
      
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Notification Types</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700">New user registrations</span>
            <input type="checkbox" className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" defaultChecked />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700">Order notifications</span>
            <input type="checkbox" className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" defaultChecked />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700">System updates</span>
            <input type="checkbox" className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700">Marketing emails</span>
            <input type="checkbox" className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
          </div>
        </div>
      </div>
      */}
    </form>
  );

  const renderSiteSetupTab = () => (
    <form onSubmit={handleSiteMetaSubmit} className="space-y-6">
      {/* Success Message */}
      {siteMetaSuccessMessage && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md">
          {siteMetaSuccessMessage}
        </div>
      )}

      {/* Error Message */}
      {siteMetaError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {siteMetaError}
        </div>
      )}

      {/* Site ID Field 
      {!siteMetaLoading && (
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Site Information</h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Site ID
            </label>
            <input
              type="text"
              name="site_id"
              value={currentSiteId}
              readOnly
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600 cursor-not-allowed"
            />
            <p className="text-xs text-gray-500 mt-2">
              This is your assigned site ID from your login data. Changes will be saved for this site only.
            </p>
          </div>
        </div>
      )}*/}

      {/* Logo Upload */}
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Site Logo</h3>
        {siteMetaLoading ? (
          <div className="flex justify-center items-center py-8">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Logo Upload
            </label>
            <div className="flex items-center space-x-4">
              <div className="flex-shrink-0">
                {formData.siteSetup.logo ? (
                  <img
                    src={`${S3_BASE_URL}/${formData.siteSetup.logo}`}
                    alt="Logo Preview"
                    className="h-24 w-24 object-contain border border-gray-300 rounded-md p-2 bg-white"
                  />
                ) : (
                  <div className="h-24 w-24 border-2 border-dashed border-gray-300 rounded-md flex items-center justify-center bg-gray-50">
                    <Image className="h-8 w-8 text-gray-400" />
                  </div>
                )}
              </div>
              <div className="flex-1">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileUpload('logo', e.target.files[0])}
                    className="hidden"
                    disabled={siteMetaSaving || uploadingLogo}
                  />
                  <div className={`btn-secondary inline-flex items-center space-x-2 ${siteMetaSaving || uploadingLogo ? 'opacity-50 cursor-not-allowed' : ''}`}>
                    {uploadingLogo ? (
                      <>
                        <LoadingSpinner />
                        <span>Uploading...</span>
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4" />
                        <span>Upload Logo</span>
                      </>
                    )}
                  </div>
                </label>
                <p className="text-xs text-gray-500 mt-2">
                  Recommended: PNG or SVG format, max 2MB
                </p>
              </div>
            </div>
          </div>

          {/* PDF Logo Upload */}
          <div className="pt-4 border-t border-gray-200">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              PDF Logo Upload
            </label>
            <div className="flex items-center space-x-4">
              <div className="flex-shrink-0">
                {formData.siteSetup.pdfLogo ? (
                  <img
                    src={`${S3_BASE_URL}/${formData.siteSetup.pdfLogo}`}
                    alt="PDF Logo Preview"
                    className="h-24 w-24 object-contain border border-gray-300 rounded-md p-2 bg-white"
                  />
                ) : (
                  <div className="h-24 w-24 border-2 border-dashed border-gray-300 rounded-md flex items-center justify-center bg-gray-50">
                    <Image className="h-8 w-8 text-gray-400" />
                  </div>
                )}
              </div>
              <div className="flex-1">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileUpload('pdfLogo', e.target.files[0])}
                    className="hidden"
                    disabled={siteMetaSaving || uploadingPdfLogo}
                  />
                  <div className={`btn-secondary inline-flex items-center space-x-2 ${siteMetaSaving || uploadingPdfLogo ? 'opacity-50 cursor-not-allowed' : ''}`}>
                    {uploadingPdfLogo ? (
                      <>
                        <LoadingSpinner />
                        <span>Uploading...</span>
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4" />
                        <span>Upload PDF Logo</span>
                      </>
                    )}
                  </div>
                </label>
                <p className="text-xs text-gray-500 mt-2">
                  Logo to be used in PDF documents
                </p>
              </div>
            </div>
          </div>
        </div>
        )}
      </div>

      {/* Title Page Colors */}
      {!siteMetaLoading && (
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Title Page Styling</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title Page Background Colour
            </label>
            <div className="flex items-center space-x-3">
              <input
                type="color"
                value={formData.siteSetup.titlePageBackgroundColor}
                onChange={(e) => handleSiteSetupChange('titlePageBackgroundColor', e.target.value)}
                className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                disabled={siteMetaSaving}
              />
              <input
                type="text"
                value={formData.siteSetup.titlePageBackgroundColor}
                onChange={(e) => handleSiteSetupChange('titlePageBackgroundColor', e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                placeholder={formData.siteSetup.titlePageBackgroundColor || "#ffffff"}
                disabled={siteMetaSaving}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title Page Text Colour
            </label>
            <div className="flex items-center space-x-3">
              <input
                type="color"
                value={formData.siteSetup.titlePageTextColor}
                onChange={(e) => handleSiteSetupChange('titlePageTextColor', e.target.value)}
                className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                disabled={siteMetaSaving}
              />
              <input
                type="text"
                value={formData.siteSetup.titlePageTextColor}
                onChange={(e) => handleSiteSetupChange('titlePageTextColor', e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                placeholder={formData.siteSetup.titlePageTextColor || "#000000"}
                disabled={siteMetaSaving}
              />
            </div>
            
          </div>
          </div>
        </div>
      )}

      {/* Reply Email */}
      {!siteMetaLoading && (
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Email Configuration</h3>
          <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Reply Email Address
          </label>
          <input
            type="email"
            value={formData.siteSetup.replyEmail}
            onChange={(e) => handleSiteSetupChange('replyEmail', e.target.value)}
            placeholder={formData.siteSetup.replyEmail || "noreply@example.com"}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
            disabled={siteMetaSaving}
          />
          <p className="text-xs text-gray-500 mt-2">
            This email address will be used as the reply-to address for automated emails
          </p>
          </div>
        </div>
      )}

      {/* Save Button */}
      {!siteMetaLoading && (
        <div className="flex justify-end">
          <button 
            type="submit" 
            className="btn-primary flex items-center space-x-2"
            disabled={siteMetaSaving}
          >
            <Save className="h-4 w-4" />
            <span>{siteMetaSaving ? 'Saving...' : 'Save Site Setup'}</span>
          </button>
        </div>
      )}
    </form>
  );

  const renderAppearanceTab = () => (
    <div className="space-y-6">
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Theme</h3>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center">
            <div className="w-16 h-16 bg-white border-2 border-gray-300 rounded-lg mx-auto mb-2"></div>
            <span className="text-sm text-gray-700">Light</span>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-gray-900 border-2 border-primary-500 rounded-lg mx-auto mb-2"></div>
            <span className="text-sm text-gray-700">Dark</span>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 border-2 border-gray-300 rounded-lg mx-auto mb-2"></div>
            <span className="text-sm text-gray-700">Auto</span>
          </div>
        </div>
      </div>

      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Language</h3>
        <select
          value={formData.language}
          onChange={(e) => handleInputChange('language', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="en">English</option>
          <option value="es">Spanish</option>
          <option value="fr">French</option>
          <option value="de">German</option>
          <option value="it">Italian</option>
        </select>
      </div>

      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Sidebar</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700">Collapsed by default</span>
            <input type="checkbox" className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700">Show icons only</span>
            <input type="checkbox" className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
          </div>
        </div>
      </div>
    </div>
  );

  const renderIntegrationsTab = () => (
    <div className="space-y-6">
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Connected Services</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold">G</span>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Google Analytics</p>
                <p className="text-xs text-gray-500">Connected</p>
              </div>
            </div>
            <button className="text-sm text-red-600 hover:text-red-700">Disconnect</button>
          </div>
          
          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold">S</span>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Stripe</p>
                <p className="text-xs text-gray-500">Connected</p>
              </div>
            </div>
            <button className="text-sm text-red-600 hover:text-red-700">Disconnect</button>
          </div>
          
          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gray-400 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold">M</span>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Mailchimp</p>
                <p className="text-xs text-gray-500">Not connected</p>
              </div>
            </div>
            <button className="btn-primary">Connect</button>
          </div>
        </div>
      </div>

      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">API Keys</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Public API Key
            </label>
            <div className="flex space-x-2">
              <input
                type="text"
                value="pk_live_1234567890abcdef"
                readOnly
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500"
              />
              <button className="btn-secondary">Copy</button>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Secret API Key
            </label>
            <div className="flex space-x-2">
              <input
                type="password"
                value="sk_live_1234567890abcdef"
                readOnly
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500"
              />
              <button className="btn-secondary">Show</button>
              <button className="btn-secondary">Copy</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return renderProfileTab();
      case 'security':
        return renderSecurityTab();
      case 'notifications':
        return renderNotificationsTab();
      case 'siteSetup':
        return renderSiteSetupTab();
      case 'updatePassword':
        return renderUpdatePasswordTab();
      case 'appearance':
        return renderAppearanceTab();
      case 'integrations':
        return renderIntegrationsTab();
      default:
        return renderProfileTab();
    }
  };

  return (
    <div className="space-y-6">
      <SubmitNotification notification={submitNotification} onDismiss={() => setSubmitNotification(null)} />
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600">Manage your account settings and preferences.</p>
      </div>

      {/* Settings Tabs */}
      <div className="card">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => {
              const isBlackTab = tab.id === 'siteSetup' || tab.id === 'notifications' || tab.id === 'updatePassword';
              return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                      ? isBlackTab 
                        ? 'border-primary-500 text-black'
                        : 'border-primary-500 text-primary-600'
                      : isBlackTab
                      ? 'border-transparent text-black hover:text-gray-900 hover:border-gray-300'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <tab.icon className="h-4 w-4" />
                  <span>{tab.name}</span>
                </div>
              </button>
              );
            })}
          </nav>
        </div>
        
        <div className="py-6">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default Settings;
